import { c as _c } from "react/compiler-runtime";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useCanViewProjectMarkups } from "@hooks/project-markups/use-can-view-project-markups";
import { Grid } from "@mui-wrapper";
import { DateDetails } from "@pages/project-details/project-overview/date-details";
import { MainProjectDetails } from "@pages/project-details/project-overview/main-project-details";
import { MarkupsSummary } from "@pages/project-details/project-overview/markups-summary/markups-summary";
import { MemberDetails } from "@pages/project-details/project-overview/member-details";
import { ProjectChart } from "@pages/project-details/project-overview/project-chart";
import { ProjectDescription } from "@pages/project-details/project-overview/project-description";
import { ProjectMap } from "@pages/project-details/project-overview/project-map/project-map";
import { ProjectThumbnailImage } from "@pages/project-details/project-overview/project-thumbnail/project-thumbnail-image";
import { SecondaryProjectDetails } from "@pages/project-details/project-overview/secondary-project-details";
import { isSelectedProjectEditableSelector } from "@store/projects/projects-selector";
import { canWriteImg360Selector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { CommonStyles } from "@styles/common-styles";
import { hasDescribeOnlyPermission } from "@utils/permission-control/project-permission-control";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { useMemo } from "react";
/** Defines the border style for all individual sections */
const borderStyle = {
  border: CommonStyles.Borders.divider
};

/** Defines the default padding inside each individual section */
const defaultPadding = {
  padding: "20px"
};

/**
 * Defines the separation between individual sections.
 * To keep things centered the separation is applied to both sides,
 * meaning that the actual separation will be two times this value.
 */
const boxesSeparation = "10px";

/**
 * Contains the Overview tab of the project details
 */
export function ProjectOverview(t0) {
  const $ = _c(83);
  const {
    project,
    isLoading
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = isSelectedProjectEditableSelector();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isProjectEditable = useAppSelector(t1);
  const {
    hasUserPermissionProjectLevel
  } = useHasUserValidPermissionProjectLevel();
  const {
    canViewCompanyMembers
  } = useHasUserValidFeatureCompanyLevel();
  const canViewMarkups = useCanViewProjectMarkups();
  const canWriteImg360 = useAppSelector(canWriteImg360Selector);
  const shouldShowSkeleton = isLoading || !project;
  const t2 = project ?? null;
  let t3;
  if ($[1] !== t2) {
    t3 = hasDescribeOnlyPermission(t2);
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isDescribeOnly = t3;
  let t4;
  bb0: {
    if (isDescribeOnly) {
      t4 = false;
      break bb0;
    }
    if (isLoading || !canWriteImg360) {
      t4 = false;
      break bb0;
    }
    let t5;
    if ($[3] !== hasUserPermissionProjectLevel || $[4] !== project) {
      t5 = hasUserPermissionProjectLevel({
        permissionName: RequiredPermissionProjectLevelName.canViewProjectsStats,
        project
      });
      $[3] = hasUserPermissionProjectLevel;
      $[4] = project;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    t4 = t5;
  }
  const shouldShowStats = t4;
  const shouldShowMarkupsSummary = !isDescribeOnly && !isLoading && canViewMarkups;
  const hasSectionsBelow = shouldShowMarkupsSummary || shouldShowStats;
  let t5;
  bb1: {
    if (isDescribeOnly) {
      t5 = false;
      break bb1;
    }
    if (isLoading) {
      t5 = false;
      break bb1;
    }
    let t6;
    if ($[6] !== canViewCompanyMembers || $[7] !== hasUserPermissionProjectLevel || $[8] !== project) {
      t6 = hasUserPermissionProjectLevel({
        permissionName: RequiredPermissionProjectLevelName.canViewAllProjectMembers,
        project
      }) || canViewCompanyMembers;
      $[6] = canViewCompanyMembers;
      $[7] = hasUserPermissionProjectLevel;
      $[8] = project;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = t6;
  }
  const shouldShowTeamMembers = t5;
  let t6;
  if ($[10] !== isDescribeOnly || $[11] !== isProjectEditable || $[12] !== project || $[13] !== shouldShowSkeleton) {
    t6 = <MainProjectDetails project={project} isProjectEditable={isProjectEditable} isLoading={shouldShowSkeleton} isDescribeOnly={isDescribeOnly} />;
    $[10] = isDescribeOnly;
    $[11] = isProjectEditable;
    $[12] = project;
    $[13] = shouldShowSkeleton;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      mt: "12px",
      width: "100%"
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      xs: 12,
      md: 5
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      ...defaultPadding,
      ...borderStyle,
      height: "100%",
      display: "flex",
      flexDirection: "column"
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== project || $[19] !== shouldShowSkeleton) {
    t10 = <DateDetails project={project} isLoading={shouldShowSkeleton} />;
    $[18] = project;
    $[19] = shouldShowSkeleton;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isDescribeOnly || $[22] !== isProjectEditable || $[23] !== project || $[24] !== shouldShowSkeleton) {
    t11 = !isDescribeOnly && <SecondaryProjectDetails project={project} isProjectEditable={isProjectEditable} isLoading={shouldShowSkeleton} />;
    $[21] = isDescribeOnly;
    $[22] = isProjectEditable;
    $[23] = project;
    $[24] = shouldShowSkeleton;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      marginBottom: "30px"
    };
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== isProjectEditable || $[28] !== project || $[29] !== shouldShowSkeleton) {
    t13 = <Grid sx={t12}><ProjectDescription project={project} isProjectEditable={isProjectEditable} isLoading={shouldShowSkeleton} /></Grid>;
    $[27] = isProjectEditable;
    $[28] = project;
    $[29] = shouldShowSkeleton;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== project || $[32] !== shouldShowSkeleton || $[33] !== shouldShowTeamMembers) {
    t14 = shouldShowTeamMembers && !shouldShowSkeleton && project.members.length > 0 && <Grid sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end"
    }}><MemberDetails /></Grid>;
    $[31] = project;
    $[32] = shouldShowSkeleton;
    $[33] = shouldShowTeamMembers;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t13 || $[38] !== t14) {
    t15 = <Grid columns={12} size={t8}><Grid sx={t9}>{t10}{t11}{t13}{t14}</Grid></Grid>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      paddingTop: {
        xs: boxesSeparation,
        md: 0
      },
      paddingLeft: {
        md: boxesSeparation
      }
    };
    t17 = {
      xs: 12,
      md: 7
    };
    $[40] = t16;
    $[41] = t17;
  } else {
    t16 = $[40];
    t17 = $[41];
  }
  const t18 = hasSectionsBelow ? boxesSeparation : 0;
  let t19;
  if ($[42] !== t18) {
    t19 = {
      xs: boxesSeparation,
      lg: t18
    };
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      lg: boxesSeparation
    };
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t19) {
    t21 = {
      paddingBottom: t19,
      paddingRight: t20,
      height: "auto"
    };
    $[45] = t19;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 6
    };
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== isDescribeOnly || $[49] !== project || $[50] !== shouldShowSkeleton) {
    t23 = project && <Grid sx={{
      ...borderStyle,
      height: "100%",
      minHeight: "200px"
    }}><ProjectMap project={project} shouldShowSkeleton={shouldShowSkeleton} isInteractive={!isDescribeOnly} /></Grid>;
    $[48] = isDescribeOnly;
    $[49] = project;
    $[50] = shouldShowSkeleton;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t21 || $[53] !== t23) {
    t24 = <Grid sx={t21} size={t22}>{t23}</Grid>;
    $[52] = t21;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const t25 = hasSectionsBelow ? boxesSeparation : 0;
  let t26;
  if ($[55] !== t25) {
    t26 = {
      ...borderStyle,
      mb: t25,
      height: "auto",
      display: "flex",
      alignItems: "center"
    };
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      xs: 12,
      sm: 12,
      md: 12,
      lg: 6
    };
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== isProjectEditable || $[59] !== project || $[60] !== shouldShowSkeleton) {
    t28 = <ProjectThumbnailImage project={project} isLoading={shouldShowSkeleton} isProjectEditable={isProjectEditable} />;
    $[58] = isProjectEditable;
    $[59] = project;
    $[60] = shouldShowSkeleton;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t26 || $[63] !== t28) {
    t29 = <Grid sx={t26} size={t27}>{t28}</Grid>;
    $[62] = t26;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== shouldShowMarkupsSummary || $[66] !== shouldShowSkeleton || $[67] !== shouldShowStats) {
    t30 = shouldShowMarkupsSummary && <Grid sx={{
      ...borderStyle,
      ...defaultPadding,
      mb: shouldShowStats ? boxesSeparation : 0
    }} size={12}><MarkupsSummary isLoading={shouldShowSkeleton} /></Grid>;
    $[65] = shouldShowMarkupsSummary;
    $[66] = shouldShowSkeleton;
    $[67] = shouldShowStats;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== shouldShowSkeleton || $[70] !== shouldShowStats) {
    t31 = shouldShowStats && <Grid sx={{
      ...borderStyle,
      ...defaultPadding
    }} size={12}><ProjectChart isLoading={shouldShowSkeleton} /></Grid>;
    $[69] = shouldShowSkeleton;
    $[70] = shouldShowStats;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t24 || $[73] !== t29 || $[74] !== t30 || $[75] !== t31) {
    t32 = <Grid container={true} columns={12} sx={t16} size={t17}>{t24}{t29}{t30}{t31}</Grid>;
    $[72] = t24;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== t15 || $[78] !== t32) {
    t33 = <Grid container={true} sx={t7}>{t15}{t32}</Grid>;
    $[77] = t15;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== t33 || $[81] !== t6) {
    t34 = <>{t6}{t33}</>;
    $[80] = t33;
    $[81] = t6;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  return t34;
}